import { useState, useEffect, useCallback } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  TablePagination,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { locationService } from '../services'
import { formatDateTime } from '../utils/dateFormat'
import ManagementSearchBar from '../components/shared/ManagementSearchBar'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import TableEmptyState from '../components/shared/TableEmptyState'

const LocationManagementPage = () => {
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)

  // Pagination State
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  const [debouncedSearch, setDebouncedSearch] = useState('')

  const applySearchQuery = useCallback((q) => {
    setDebouncedSearch(q)
    setPage(0)
  }, [])

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [editFormData, setEditFormData] = useState({})
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [associations, setAssociations] = useState(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [needsCascadeConfirm, setNeedsCascadeConfirm] = useState(false)

  const fetchLocations = async () => {
    try {
      setLoading(true)
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      }

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim()
      }

      const data = await locationService.getLocations(params)

      if (data && data.items) {
        setLocations(data.items)
        setTotalItems(data.pagination?.total || 0)
      } else {
        setLocations(data || [])
        setTotalItems(data?.length || 0)
      }
    } catch (error) {
      console.error('Error fetching locations:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLocations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleEditClick = (loc) => {
    setSelectedLocation(loc)
    const formattedGeolocation = loc.geolocation
      ? (typeof loc.geolocation === 'object'
        ? `${loc.geolocation.x}, ${loc.geolocation.y}`
        : String(loc.geolocation))
      : ''

    setEditFormData({
      name: loc.name || '',
      code: loc.code || '',
      geolocation: formattedGeolocation
    })
    setEditDialogOpen(true)
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setSelectedLocation(null)
    setEditFormData({})
    setActionError(null)
  }

  const handleEditChange = (field, value) => {
    setEditFormData({ ...editFormData, [field]: value })
  }

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true)
      setActionError(null)

      if (!selectedLocation || !selectedLocation.id) {
        setActionError('Location ID is required for update')
        return
      }

      await locationService.updateLocation(selectedLocation.id, editFormData)

      setSuccessMessage('Location updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)

      handleEditClose()
      fetchLocations()
    } catch (err) {
      console.error('Error updating location:', err)
      setActionError(err.response?.data?.message || 'Failed to update location')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteClick = (loc) => {
    setSelectedLocation(loc)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      setActionLoading(true)
      setActionError(null)

      // If we need cascade confirmation but text is incorrect, don't proceed
      if (needsCascadeConfirm && deleteConfirmText !== 'DELETE') {
        setActionError('Please type DELETE to confirm cascade deletion')
        return
      }

      const response = await locationService.deleteLocation(
        selectedLocation.id,
        needsCascadeConfirm // pass true if we already showed the warning
      )

      if (response && response.success) {
        setSuccessMessage(response.message || 'Location deleted successfully')
        setTimeout(() => setSuccessMessage(null), 3000)

        setDeleteConfirmOpen(false)
        setSelectedLocation(null)
        setNeedsCascadeConfirm(false)
        setAssociations(null)
        setDeleteConfirmText('')
        fetchLocations()
      } else if (response && response.requiresConfirmation) {
        // Switch to cascade confirmation mode
        setNeedsCascadeConfirm(true)
        setAssociations(response.associations)
        setActionError(response.message)
      } else {
        setActionError(response?.message || 'Unknown error occurred')
      }
    } catch (err) {
      console.error('Error deleting location:', err)
      setActionError(err.response?.data?.message || 'Failed to delete location')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setSelectedLocation(null)
    setNeedsCascadeConfirm(false)
    setAssociations(null)
    setDeleteConfirmText('')
    setActionError(null)
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 1 }}>
            Manage Locations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View all managed global locations
          </Typography>
        </Box>
        <Tooltip title="Refresh Locations">
          <IconButton onClick={fetchLocations} disabled={loading} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <ManagementSearchBar
        label="Search by Code or Name"
        placeholder="Enter location code or name..."
        onApplySearchQuery={applySearchQuery}
      />

      <Card sx={{
        borderRadius: 2,
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
      }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Code (Unique)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Geolocation (Lat, Lng)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Modified</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingRow colSpan={6} />
                ) : locations.length === 0 ? (
                  <TableEmptyState
                    colSpan={6}
                    message="No locations found. Upload a CSV to get started."
                  />
                ) : (
                  locations.map((loc) => (
                    <TableRow key={loc.id} hover>
                      <TableCell sx={{ width: 50 }}>
                        <LocationOnIcon sx={{ color: '#e90000', opacity: 0.8 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {loc.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block' }}>
                          {loc.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {loc.geolocation
                            ? (typeof loc.geolocation === 'object'
                              ? `${loc.geolocation.x}, ${loc.geolocation.y}`
                              : String(loc.geolocation))
                            : 'Not provided'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(loc.date_modified)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit Location">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(loc)}
                              sx={{ color: '#1976d2' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Location">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(loc)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      {/* Edit Location Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>Edit Location</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label="Location Name"
              value={editFormData.name || ''}
              onChange={(e) => handleEditChange('name', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              required
            />

            <TextField
              label="Location Code"
              value={editFormData.code || ''}
              onChange={(e) => handleEditChange('code', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              required
            />

            <TextField
              label="Geolocation"
              value={editFormData.geolocation || ''}
              onChange={(e) => handleEditChange('geolocation', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              placeholder="e.g. 23.8103, 90.4125"
              helperText="Format: latitude, longitude"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, mt: 2 }}>
          <Button onClick={handleEditClose} disabled={actionLoading} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={actionLoading || !editFormData.name?.trim() || !editFormData.code?.trim()}
            sx={{
              bgcolor: '#e90000',
              '&:hover': { bgcolor: '#cc0000' }
            }}
          >
            {actionLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
          {needsCascadeConfirm ? '⚠️ CASCADE DELETE WARNING' : 'Delete Location'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete the location <strong>{selectedLocation?.name}</strong> ({selectedLocation?.code})?
          </Typography>

          {needsCascadeConfirm && associations && (
            <Box sx={{ bgcolor: '#fff5f5', p: 2, borderRadius: 1, border: '1px solid #ffcdd2', mb: 2 }}>
              <Typography variant="subtitle2" color="error" sx={{ fontWeight: 'bold', mb: 1 }}>
                ASSOCIATED DATA DETECTED:
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 1, pl: 2 }}>
                {associations.inventoryCount > 0 && (
                  <li>
                    <Typography variant="body2" color="error.main">
                      {associations.inventoryCount} Inventory Lot(s) will be PERMANENTLY DELETED.
                    </Typography>
                  </li>
                )}
                {associations.deliveryTimeCount > 0 && (
                  <li>
                    <Typography variant="body2" color="error.main">
                      {associations.deliveryTimeCount} Delivery Time Setting(s) will be PERMANENTLY DELETED.
                    </Typography>
                  </li>
                )}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                This action is irreversible and will delete all related records.
              </Typography>
            </Box>
          )}

          {!needsCascadeConfirm && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          )}

          {needsCascadeConfirm && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                Please type <strong>DELETE</strong> to confirm this action:
              </Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE here..."
                autoFocus
                error={deleteConfirmText !== '' && deleteConfirmText !== 'DELETE'}
              />
            </Box>
          )}

          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: needsCascadeConfirm ? '#fafafa' : 'inherit' }}>
          <Button onClick={handleDeleteCancel} disabled={actionLoading} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={
              actionLoading ||
              (needsCascadeConfirm && deleteConfirmText !== 'DELETE')
            }
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              bgcolor: needsCascadeConfirm ? '#d32f2f' : '#e90000',
              fontWeight: 'bold'
            }}
          >
            {actionLoading ? 'Deleting...' : (needsCascadeConfirm ? 'CONFIRM' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  )
}

export default LocationManagementPage
